<template>
   <div class="flex flex-row">
      <div v-for="(cta, index) in ctasWithStyle" :key="`cta-items-${index}`" class="mr-2 last:mr-0">
         <LinkButton
            v-if="cta.type === 'link'"
            v-bind="cta"
            :localeId="localeId"
            class="w-fit"
            data-cy="cta-items-link"
         />
         <DefaultButton
            v-else
            v-bind="cta"
            class="w-fit"
            data-cy="cta-items-button"
            @click="($event) => onButtonClick(cta)"
         />
      </div>
   </div>
</template>

<script setup lang="ts">
import { inject } from "vue";
import { intercomKey } from "@/plugins/useIntercom";
// composables & stores
import { onCTAClick } from "@/components/ui/buttons/button.composable";
import { useAuthStore } from "@/stores/authStore";
//types
import type { LinkButtonProp, LinkButtonCTAData } from "@/components/ui/buttons/LinkButton.vue";
import type { DefaultButtonProp, DefaultButtonCTAData } from "@/components/ui/buttons/DefaultButton.vue";
import { Theme, type ContainerTheme } from "@/types";

export type CTAItem = LinkButtonProp | DefaultButtonProp;
export type CTAItemData = LinkButtonCTAData | DefaultButtonCTAData;

const intercom = inject(intercomKey);
const authStore = useAuthStore();

// props
// -----------------------------------------
const props = withDefaults(
   defineProps<{
      ctas: CTAItem[];
      theme?: ContainerTheme;
      isPageCta: boolean; // page | card CTA (card CTA has a smaller size)
      localeId?: string | null;
   }>(),
   {
      theme: Theme.White,
      localeId: null,
   }
);

// computed
// -----------------------------------------
// if any of the CTA's should not be visible, remove them from the CTA's array
const visibleCtas = computed(() => {
   if (!props.ctas || props.ctas.length === 0) return [];

   const visibleCtas = props.ctas.filter((cta) => {
      if (cta.hideOn === "logged_in" && authStore.authTokenExists) return false;
      else if (cta.hideOn === "logged_out" && !authStore.authTokenExists) return false;
      else return true;
   });

   return visibleCtas;
});

const ctasWithStyle = computed(() => {
   if (visibleCtas.value.length === 0) return [];

   const ctasWithStyle = [];

   if (visibleCtas.value[0]) {
      const primaryCta = getCtaStyle(props.theme, props.isPageCta, "primary");
      ctasWithStyle.push({
         ...props.ctas[0],
         ...primaryCta,
      });
   }

   if (visibleCtas.value[1]) {
      const secondaryCta = getCtaStyle(props.theme, props.isPageCta, "secondary");
      ctasWithStyle.push({
         ...props.ctas[1],
         ...secondaryCta,
      });
   }

   return ctasWithStyle;
});

// methods
// -----------------------------------------
const onButtonClick = (cta) => {
   onCTAClick(cta.action, intercom);
};

// given a theme and whether the CTA is primary/secondary, get the correct object of: color and styleType
const getCtaStyle = (theme, isPageCta, ctaType) => {
   const ctaStyle = {
      color: Theme.BrandDark,
      styleType: ctaType === "primary" ? "filled" : "outlined",
      size: isPageCta ? "md" : "sm", // small for cards
   };

   if (!theme) return ctaStyle;

   // Change to object mapping.
   const themeToColorMap = {
      white: Theme.BrandDark,
      "brand-dark": Theme.WhiteBrand,
      grey: Theme.Black,
      black: Theme.Grey,
      pink: Theme.PinkDark,
      purple: Theme.PurpleDark,
      green: Theme.GreenDark,
      tan: Theme.TanDark,
      sand: Theme.SandDark,
      "green-dark": Theme.Green,
      "gradient-orange": Theme.PinkDark,
      "pink-dark": Theme.Pink,
      "purple-dark": Theme.Purple,
   };

   if (theme in themeToColorMap) {
      ctaStyle.color = themeToColorMap[theme];
   }

   return ctaStyle;
};
</script>

<style scoped></style>
